import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Users, Clock, Zap, BarChart3, Target, Play, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import DemoForm from "@/components/forms/DemoForm";

// Metrics data
const metrics = [
  { value: "+20%", label: "RevPAR", icon: TrendingUp },
  { value: "+15%", label: "Ocupación", icon: Users },
  { value: "+30%", label: "Eficiencia Operativa", icon: Clock },
];

// Problems data
const problems = [
  {
    title: "Precios mal optimizados",
    description: "¿Tus tarifas no responden a la demanda del mercado en tiempo real?",
  },
  {
    title: "Baja ocupación",
    description: "¿Tienes habitaciones vacías mientras la competencia llena?",
  },
  {
    title: "Falta de automatización",
    description: "¿Pierdes horas ajustando precios manualmente sin resultados claros?",
  },
];

// Solutions/Benefits data
const solutions = [
  {
    icon: Zap,
    title: "Pricing Dinámico Automatizado",
    description: "Ajusta tarifas en tiempo real basándote en demanda, competencia y eventos locales.",
  },
  {
    icon: BarChart3,
    title: "Forecasting Inteligente",
    description: "Predice la demanda con precisión usando modelos de machine learning avanzados.",
  },
  {
    icon: Target,
    title: "Aumento de Ingresos",
    description: "Maximiza tu RevPAR capturando el valor óptimo de cada habitación.",
  },
];

// How it works steps
const steps = [
  {
    number: "01",
    title: "Conecta tu PMS",
    description: "Integración rápida con los principales sistemas de gestión hotelera.",
  },
  {
    number: "02",
    title: "Analiza tus datos",
    description: "Nuestro sistema procesa tu información histórica y del mercado.",
  },
  {
    number: "03",
    title: "Optimiza automáticamente",
    description: "Recibe recomendaciones de precios y deja que el sistema optimice por ti.",
  },
];

export default function HomePage() {
  useEffect(() => {
    document.title = "Zimoo | Software de Revenue Management para Hoteles";
  }, []);

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative flex items-center pt-20 pb-8 bg-gradient-to-b from-[#FAFAFA] to-white grain-overlay" data-testid="hero-section">
        <div className="max-w-7xl mx-auto container-padding py-8 md:py-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Hero Content */}
            <div className="animate-slide-up">
              <span className="label-text mb-4 block">REVENUE MANAGEMENT HOTELERO</span>
              <h1 className="heading-1 mb-6">
                Software de Revenue Management para{" "}
                <span className="text-gradient">Hoteles</span>
              </h1>
              <p className="body-text mb-8 max-w-lg">
                Optimiza precios, aumenta tu RevPAR y automatiza tu estrategia de ingresos con inteligencia artificial.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  className="btn-primary text-base px-8 py-4" 
                  data-testid="hero-cta-primary"
                  onClick={() => document.getElementById('demo-form')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Contáctanos
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Link to="/software-rms">
                  <Button variant="outline" className="btn-secondary text-base px-8 py-4" data-testid="hero-cta-secondary">
                    <Play className="mr-2 h-5 w-5" />
                    Ver cómo funciona
                  </Button>
                </Link>
              </div>
              
            </div>

            {/* Hero Image */}
            <div className="relative animate-fade-in stagger-2">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1768223933860-6d62bc5b2ff3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBsdXh1cnklMjBob3RlbCUyMGV4dGVyaW9yfGVufDB8fHx8MTc3NDI5NDc5Mnww&ixlib=rb-4.1.0&q=85&w=800"
                  alt="Hotel moderno de lujo"
                  className="w-full h-auto"
                />
                {/* Floating metrics card */}
                <div className="absolute -bottom-6 -left-6 md:bottom-6 md:left-6 bg-white rounded-xl shadow-lg p-4 border border-black/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#0A0A0A]">+28%</p>
                      <p className="text-sm text-[#404040]">RevPAR promedio</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="section-padding bg-white" data-testid="problem-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-16">
            <span className="label-text mb-4 block">EL PROBLEMA</span>
            <h2 className="heading-2 mb-4">
              ¿Tu hotel enfrenta estos desafíos?
            </h2>
            <p className="body-text max-w-2xl mx-auto">
              La mayoría de los hoteles pierden ingresos significativos por no tener una estrategia de revenue management efectiva.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {problems.map((problem, index) => (
              <div
                key={index}
                className="card-hover p-8 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-red-500">!</span>
                </div>
                <h3 className="text-xl font-semibold text-[#0A0A0A] mb-3">
                  {problem.title}
                </h3>
                <p className="text-[#404040]">{problem.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="section-padding bg-[#FAFAFA]" data-testid="solution-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-16">
            <span className="label-text mb-4 block">LA SOLUCIÓN</span>
            <h2 className="heading-2 mb-4">
              Zimoo optimiza tus ingresos <span className="text-gradient">automáticamente</span>
            </h2>
            <p className="body-text max-w-2xl mx-auto">
              Nuestra plataforma de Revenue Management utiliza inteligencia artificial para maximizar tus ingresos mientras reduces el trabajo manual.
            </p>
          </div>

          <div className="bento-grid">
            {solutions.map((solution, index) => (
              <div
                key={index}
                className="card-hover p-8"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#1ABCB4] to-[#5200FF] flex items-center justify-center mb-6">
                  <solution.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#0A0A0A] mb-3">
                  {solution.title}
                </h3>
                <p className="text-[#404040]">{solution.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="py-16 bg-[#0A0A0A]" data-testid="metrics-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {metrics.map((metric, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                  <metric.icon className="h-8 w-8 text-[#1ABCB4]" />
                </div>
                <p className="text-5xl md:text-6xl font-bold text-white mb-2">
                  {metric.value}
                </p>
                <p className="text-gray-400 text-lg">{metric.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="section-padding bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-16">
            <span className="label-text mb-4 block">CÓMO FUNCIONA</span>
            <h2 className="heading-2 mb-4">
              3 pasos simples para optimizar tus ingresos
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-8xl font-bold text-[#FAFAFA] absolute -top-4 left-0">
                  {step.number}
                </div>
                <div className="relative pt-12 pl-4">
                  <h3 className="text-xl font-semibold text-[#0A0A0A] mb-3">
                    {step.title}
                  </h3>
                  <p className="text-[#404040]">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-4 h-8 w-8 text-[#1ABCB4]" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with Form */}
      <section id="demo-form" className="section-padding bg-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div>
              <span className="label-text mb-4 block">COMIENZA HOY</span>
              <h2 className="heading-2 mb-6">
                Agenda una demo <span className="text-gradient">personalizada</span>
              </h2>
              <p className="body-text mb-8">
                Descubre cómo Zimoo puede aumentar los ingresos de tu hotel. Nuestro equipo te mostrará la plataforma y responderá todas tus preguntas.
              </p>
              
              <ul className="space-y-4">
                {[
                  "Demo personalizada para tu hotel",
                  "Análisis gratuito de oportunidades",
                  "Sin compromiso ni costos ocultos",
                  "Soporte en español 24/7"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-[#404040]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Form */}
            <div>
              <DemoForm sourcePage="home" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
